import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { fetchEntityDetail } from "../../../components/global-entities/globalEntitiesSlice";
import {
  fetchDocumentFolders,
  setBlobFilterStates,
} from "../../Process/actions/attachmentsSlice";
import apiService from "~/services/service";
import z from "zod";
import API_ROUTES from "~/api-routes";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { fetchDirectors } from "~/components/directors-form/directorsSlice";

const useFileDocuments = ({
  id,
  service_id = null,
  process_id = null,
  type = null,
  request_type = null,
  request_id = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(setBlobFilterStates({}));
  }, []);
  const { entity_detail, document_folders, processAttachments } = useSelector(
    (state) => ({
      entity_detail: state.globalEntities.entity_detail,
      document_folders: state.attachments.document_folders,
      processAttachments: state.attachments.processAttachments,
    }),
  );

  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [processName, setProcessName] = useState(null);
  const entityData = useSelector((state) => state.globalEntities.entity_detail);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [requests, setRequests] = useState([]);
  const [selectedRequestType, setSelectedRequestType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isPayslipFolder, setIsPayslipFolder] = useState(false);
  const { value: isPayslipUploadFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.PAYSLIP_UPLOAD,
    false,
  );
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(fetchEntityDetail(id)),
        dispatch(fetchDocumentFolders(id)),
        fetchRequests(),
      ]);
      setLoading(false);
    };
    fetchData();
  }, [id, dispatch]);

  useEffect(() => {
    if (service_id && type && request_type && request_id) {
      setSelectedRequest(null);
    } else if (type && service_id && (process_id || request_type)) {
      setSelectedProcess(null);
      setSelectedRequestType(null);
    } else if (type && service_id) {
      setSelectedType(null);
    } else if (service_id) {
      setSelectedService(null);
    }
  }, [type, service_id, process_id, request_type, request_id]);

  useEffect(() => {
    if (location.pathname.includes("payslips")) {
      setIsPayslipFolder(true);
    } else {
      setIsPayslipFolder(false);
    }
  }, [location.pathname]);

  const hasEntityPlusAccess = entityData?.active_services?.some(
    (e) => e.name === "Entities +",
  );

  const requestTypeSubFolders = [
    {
      name: "Service Query",
      code: "service-query",
      id: "SERVICE_QUERY",
    },
    {
      name: "New / Additional Service Request",
      code: "additional-service-request",
      id: "ADDITIONAL_SERVICE_REQUEST",
    },
    {
      name: "Contract / Fees Query",
      code: "fees-query",
      id: "FEES_QUERY",
    },
    {
      name: "Tech Support (IT/Helpdesk)",
      code: "tech-support",
      id: "TECH_SUPPORT",
    },
  ];

  useEffect(() => {
    const foldersdata = document_folders.map((folder) => ({
      ...folder,
      isProcess: true,
    }));
    if (hasEntityPlusAccess) {
      let entityobj = {
        id: "a9c07a03-326c-45e5-b644-a795f0cbe67r",
        name: "Core Entity Information",
        count: 0,
        type: [
          {
            name: "Entity Information",
            code: "entity-information",
            type: "ENTITIES_PLUS",
            subFolders: null,
          },
          {
            name: "Shareholder",
            code: "shareholder",
            type: "SHAREHOLDER",
            subFolders: null,
          },
          {
            name: "Officers",
            code: "officers",
            type: "OFFICER",
            subFolders: null,
          },
          {
            name: "Power of Attorney",
            code: "power-of-attorney",
            type: "OFFICER",
            subFolders: null,
          },
          {
            name: "Directors",
            code: "directors",
            type: "DIRECTOR",
            subFolders: null,
          },
        ],
      };
      foldersdata?.unshift(entityobj);
    }
    let adhoc = {
      id: "a9c07a03-326c-45e5-b644-a795f0cbe67f",
      name: "Ad Hoc Requests",
      count: 0,
      process: null,
    };
    foldersdata?.push(adhoc);

    if (!service_id) {
      setFolders(foldersdata);
    } else if (selectedService?.name === "Payroll" && selectedProcess) {
      const subFolder = isPayslipUploadFeatureEnabled
        ? selectedProcess?.subFolders
        : selectedProcess?.subFolders?.filter((folder) => {
            folder?.name !== "Payslips";
          });
      setFolders(subFolder);
    } else {
      const serviceFolder = foldersdata?.find((f) => f.id === service_id);
      if (serviceFolder?.name === "Core Entity Information") {
        setSelectedService(serviceFolder);
        if (!type) {
          setFolders(serviceFolder?.type);
        } else {
          const selectedtype = serviceFolder?.type?.find((f) => f.code == type);
          setSelectedType(selectedtype);
          getUserList();
        }
      } else if (serviceFolder?.name === "Ad Hoc Requests") {
        if (requests.length === 0) {
          setSelectedService(serviceFolder);
          setFolders([]);
          return;
        }
        const grouped = requests?.reduce((acc, req) => {
          const year = new Date(req.createdAt).getFullYear();
          if (!acc[year]) {
            acc[year] = {
              name: year,
              type: "Ad Hoc Requests",
              requestTypeFolders: requestTypeSubFolders,
            };
          }
          return acc;
        }, {});
        const groupedArray = Object.values(grouped || {});
        setSelectedService(serviceFolder);
        if (!type) {
          setFolders(groupedArray);
        } else if (request_type) {
          setSelectedType(groupedArray?.find((f) => f.name == type));
          setRequestFolders();
        } else {
          const selectedyear = groupedArray?.find((f) => f.name == type);
          setSelectedType(selectedyear);
          setFolders(selectedyear?.requestTypeFolders);
        }
      } else {
        const grouped = serviceFolder?.process?.reduce((acc, process) => {
          const year = new Date(process.start_date).getFullYear();
          if (!acc[year]) {
            acc[year] = {
              name: year,
              count: 0,
              isProcess: true,
              process: [],
            };
          }
          acc[year].count += process.count;
          acc[year].process.push({ ...process, isProcess: true });
          return acc;
        }, {});
        const groupedArray = Object.values(grouped || {});
        setSelectedService(serviceFolder);

        if (!type) {
          setFolders(groupedArray);
        } else {
          const selectedyear = groupedArray?.find((f) => f.name == type);
          setSelectedType(selectedyear);
          setFolders(selectedyear?.process);
        }
      }
    }
  }, [document_folders, requests]);

  const setRequestFolders = () => {
    const key = Object.keys(reasonTypes).find(
      (key) => key.toLowerCase() === request_type.toLowerCase(),
    );
    const value = reasonTypes[key];

    const requestSubFolders = requests
      .filter((r) => r.reason === value)
      .map((r) => ({
        ...r,
        name: r.request_id,
      }));

    setFolders(requestSubFolders);
  };

  const getUserList = async () => {
    switch (selectedType?.code) {
      case "shareholder": {
        const data = await fetchShareholders();
        const userList = data?.map((shareholder) => {
          const shareholderName =
            shareholder.type === "Individual"
              ? shareholder.preferred_name
                ? shareholder.preferred_name
                : `${shareholder.name}${shareholder.last_name ? " " + shareholder.last_name : ""}`
              : shareholder.legal_name;
          return {
            id: shareholder.id,
            shareholderType: shareholder.type,
            name: shareholderName,
            type: "SHAREHOLDER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        setFolders(userList);
        break;
      }
      case "directors": {
        const { data } = await dispatch(fetchDirectors({ id })).unwrap();
        const userList = data
          ?.filter((director) =>
            director.appointments?.some(
              (appointment) => appointment.entity_id === id,
            ),
          )
          .map((director) => {
            const directorName = director.preferred_name
              ? director.preferred_name
              : `${director.name}${director.last_name ? " " + director.last_name : ""}`;
            return {
              id: director.id,
              shareholderType: director.type,
              name: directorName,
              type: "DIRECTOR",
            };
          });
        setSelectedType({ ...selectedType, subFolders: userList });
        setFolders(userList);
        break;
      }
      case "officers": {
        const records = await fetchOfficersOrPowerOfAttorney("officer");
        const userList = records?.map((officer) => {
          const officerName = officer.preferred_name
            ? officer.preferred_name
            : `${officer.name}${officer.last_name ? " " + officer.last_name : ""}`;
          return {
            id: officer.id,
            name: officerName,
            type: "OFFICER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        setFolders(userList);
        break;
      }
      case "power-of-attorney": {
        const records =
          await fetchOfficersOrPowerOfAttorney("power-of-attorney");
        const userList = records?.map((poa) => {
          const poaName = poa.preferred_name
            ? poa.preferred_name
            : `${poa.name}${poa.last_name ? " " + poa.last_name : ""}`;
          return {
            id: poa.id,
            name: poaName,
            type: "OFFICER",
          };
        });
        setSelectedType({ ...selectedType, subFolders: userList });
        setFolders(userList);
        break;
      }
    }
  };

  const fetchShareholders = async () => {
    const { data } = await apiService(API_ROUTES.SHARE_HOLDERS, "GET", {
      replace: { id },
    });
    return data;
  };

  const fetchOfficersOrPowerOfAttorney = async (type) => {
    const {
      data: { records },
    } = await apiService(API_ROUTES.FETCH_OFFICERS, "GET", {
      replace: {
        entity_id: id,
        page: 1,
        pageSize: 100,
        searchValue: "",
        type,
        sortBy: "name",
        sortOrder: "ASC",
      },
      schema: z.object({
        page: z.number().optional(),
        pageSize: z.number().optional(),
        search: z.string().optional(),
        type: z.enum(["officer", "power-of-attorney"]),
        sortBy: z.enum(["name", "updatedAt", "role"]).optional(),
        sortOrder: z.enum(["ASC", "DESC"]).optional(),
      }),
    });
    return records;
  };

  const fetchRequests = async () => {
    let {
      data: { requests },
    } = await apiService(API_ROUTES.FETCH_REQUESTS, "GET", {
      replace: { page: 1, pageSize: 100, filter: "all", requestType: "AD_HOC" },
    });
    requests = requests.filter((req) => req.entityData?.id === id);
    setRequests(requests);
  };

  const handleClickService = (folder) => {
    if (folder?.name === "Ad Hoc Requests") {
      navigate(`requests/${folder?.id}`);
    } else {
      navigate(`services/${folder?.id}`);
    }
  };

  const reasonTypes = {
    SERVICE_QUERY: "Service Query",
    ADDITIONAL_SERVICE_REQUEST: "New / Additional Service Request",
    FEES_QUERY: "Contract / Fees query",
    TECH_SUPPORT: "Tech Support (IT/Helpdesk)",
    IMPLEMENTATION: "Implementation",
  };

  const handleClickType = (folder) => {
    if (selectedService?.name === "Core Entity Information") {
      navigate(`${folder?.id}`);
    } else if (selectedService?.name === "Ad Hoc Requests") {
      const matchingKey = Object.entries(reasonTypes).find(
        ([, value]) => value.toLowerCase() === folder?.name?.toLowerCase(),
      )?.[0];
      if (matchingKey) navigate(`request-type/${matchingKey.toLowerCase()}`);
    } else {
      navigate(`process/${folder?.id}`);
    }
  };

  const handleClickPayslip = (folder) => {
    navigate(`payslips`);
  };

  const handleClickRequest = (folder) => {
    navigate(`${folder.id}`);
  };

  const handleClickServiceProcess = (process) => {
    setSelectedType(process);
    navigate(`${process.name}`);
  };

  const handleClickServiceSubFolder = (folder) => {
    setSelectedType(folder);
    navigate(`${folder.code}`);
  };

  const handleBackClick = () => {
    if (isPayslipFolder && type && service_id && process_id) {
      navigate(
        `/files/entity/${id}/services/${service_id}/${type}/process/${process_id}`,
      );
    } else if (service_id && type && request_type && request_id) {
      setSelectedRequest(null);
      navigate(
        `/files/entity/${id}/requests/${service_id}/${type}/request-type/${request_type}`,
      );
    } else if (type && service_id && (process_id || request_type)) {
      setSelectedProcess(null);
      setSelectedRequestType(null);
      navigate(
        `/files/entity/${id}/${selectedService?.name === "Ad Hoc Requests" ? "requests" : "services"}/${service_id}/${type}`,
      );
    } else if (type && service_id) {
      setSelectedType(null);
      navigate(
        `/files/entity/${id}/${selectedService?.name === "Ad Hoc Requests" ? "requests" : "services"}/${service_id}`,
      );
    } else if (service_id) {
      setSelectedService(null);
      navigate(`/files/entity/${id}`);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (!process_id) return;
    const process =
      selectedService?.name !== "Core Entity Information"
        ? selectedType?.process?.find((x) => x.id == process_id)
        : selectedType?.subFolders?.find((x) => x.id == process_id);
    setSelectedProcess(process);
    setProcessName(process?.name);
  }, [process_id, selectedService, selectedType]);

  useEffect(() => {
    if (!type || !folders || selectedType) return;
    if (selectedService?.name === "Ad Hoc Requests") {
      const typeSelected = folders?.find((f) => f.name == type);
      if (typeSelected) setSelectedType(typeSelected);
    }
  }, [type, folders, selectedType]);

  useEffect(() => {
    if (!request_type || selectedRequestType) return;
    const key = Object.keys(reasonTypes).find(
      (key) => key.toLowerCase() === request_type.toLowerCase(),
    );
    const requestType = requestTypeSubFolders?.find((x) => x.id == key);
    if (requestType) setSelectedRequestType(requestType);
  }, [request_type, selectedRequestType]);

  useEffect(() => {
    if (!request_id || selectedRequest) return;
    const request = requests?.find((x) => x.id == request_id);
    if (request) {
      setSelectedRequest({ ...request, name: request.request_id });
    }
  }, [request_id, requests, selectedRequest]);

  const handleChangePage = (e) => {
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleOpenRequest = () => {
    navigate(`/requests/${selectedRequest?.id}/conversation`);
  };

  const handleOpenProcess = () => {
    navigate(`/process/${selectedProcess?.id}/milestones`);
  };

  return {
    states: {
      entity_detail,
      folders,
      loading,
      selectedService,
      processAttachments,
      selectedProcess,
      selectedType,
      selectedRequestType,
      selectedRequest,
      isPayslipFolder,
    },
    handleClickService,
    handleClickServiceProcess,
    handleClickServiceSubFolder,
    handleBackClick,
    handleClickType,
    fileName,
    setFileName,
    dispatch,
    processName,
    first,
    rows,
    onPageChange: handleChangePage,
    handleClickRequest,
    handleOpenRequest,
    handleOpenProcess,
    handleClickPayslip,
  };
};

export default useFileDocuments;
